/**
 * Permission Matrix Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Grid editor for managing role permissions by resource and action.
 */

import React from 'react';
import { Check, Minus } from 'lucide-react';
import type { PermissionGroup } from '../../../types/admin';

interface PermissionMatrixProps {
  groups: PermissionGroup[];
  onChange: (groups: PermissionGroup[]) => void;
  disabled?: boolean;
}

const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  groups,
  onChange,
  disabled = false,
}) => {
  // Toggle a single permission
  const togglePermission = (groupIndex: number, permIndex: number) => {
    if (disabled) return;

    const newGroups = [...groups];
    const perm = newGroups[groupIndex].permissions[permIndex];
    perm.enabled = !perm.enabled;
    onChange(newGroups);
  };

  // Toggle all permissions for a resource
  const toggleAllForResource = (groupIndex: number) => {
    if (disabled) return;

    const newGroups = [...groups];
    const group = newGroups[groupIndex];
    const allEnabled = group.permissions.every((p) => p.enabled);

    group.permissions.forEach((p) => {
      p.enabled = !allEnabled;
    });

    onChange(newGroups);
  };

  // Check if all permissions for a resource are enabled
  const isAllEnabled = (group: PermissionGroup) => {
    return group.permissions.every((p) => p.enabled);
  };

  // Check if some permissions for a resource are enabled
  const isSomeEnabled = (group: PermissionGroup) => {
    return group.permissions.some((p) => p.enabled) && !isAllEnabled(group);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {groups.map((group, groupIndex) => (
        <div
          key={group.resource}
          className={groupIndex > 0 ? 'border-t border-gray-200' : ''}
        >
          {/* Resource header */}
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900 capitalize">{group.resource}</h4>
              <p className="text-sm text-gray-500">{group.description}</p>
            </div>
            <button
              onClick={() => toggleAllForResource(groupIndex)}
              disabled={disabled}
              className={`
                w-6 h-6 rounded flex items-center justify-center border
                transition-colors
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                ${isAllEnabled(group)
                  ? 'bg-[#002B45] border-[#002B45] text-white'
                  : isSomeEnabled(group)
                  ? 'bg-gray-100 border-gray-300 text-gray-600'
                  : 'bg-white border-gray-300 text-transparent hover:border-gray-400'
                }
              `}
              title={isAllEnabled(group) ? 'Disable all' : 'Enable all'}
            >
              {isAllEnabled(group) ? (
                <Check size={14} />
              ) : isSomeEnabled(group) ? (
                <Minus size={14} />
              ) : null}
            </button>
          </div>

          {/* Permissions grid */}
          <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {group.permissions.map((perm, permIndex) => (
              <label
                key={perm.permission}
                className={`
                  flex items-center gap-2 p-2 rounded border cursor-pointer
                  transition-colors
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  ${perm.enabled
                    ? 'border-[#002B45] bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <input
                  type="checkbox"
                  checked={perm.enabled}
                  onChange={() => togglePermission(groupIndex, permIndex)}
                  disabled={disabled}
                  className="sr-only"
                />
                <div
                  className={`
                    w-4 h-4 rounded flex items-center justify-center border flex-shrink-0
                    ${perm.enabled
                      ? 'bg-[#002B45] border-[#002B45] text-white'
                      : 'bg-white border-gray-300'
                    }
                  `}
                >
                  {perm.enabled && <Check size={10} />}
                </div>
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-900 capitalize">
                    {perm.action.replace(/_/g, ' ')}
                  </span>
                  <p className="text-xs text-gray-500 truncate" title={perm.description}>
                    {perm.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PermissionMatrix;
